import type { ReactNode } from "react";
import { useTheme } from "@/api/queries";
import { useColorScheme } from "./colorScheme";
import { ThemeContext } from "./context";

/**
 * Makes the deployment's brand available to components.
 *
 * The *visual* tokens never pass through React — they arrive as CSS custom
 * properties from /api/theme.css, a render-blocking stylesheet, so the first
 * paint is already themed. This context carries only the parts that are
 * content rather than style: names, logos and the chart palettes.
 */
export function ThemeProvider({
  children,
  keepTitle = false,
}: {
  children: ReactNode;
  /** Leave `document.title` alone — a standalone report is titled by its report. */
  keepTitle?: boolean;
}) {
  const { data: theme } = useTheme();
  const scheme = useColorScheme((state) => state.resolved);

  const productName = theme?.brand.name ?? "";
  const assets = theme?.assets;
  // The same substitution the server does for the email footer, so a client's
  // copy reads identically in the app and in what lands in an inbox.
  const resolve = (text: string | undefined | null) =>
    (text ?? "")
      .replaceAll("{name}", productName)
      .replaceAll("{vendor}", theme?.brand.vendor ?? "");
  // Each scheme falls back to the other's logo — the branding page has always
  // promised it. Without the fallback, a deployment that uploaded one logo
  // showed the platform's drawn mark to every dark-mode reader, including
  // everyone who opened an exported HTML file with their system in dark mode.
  const logo =
    (scheme === "dark"
      ? (assets?.logo_dark ?? assets?.logo_light)
      : (assets?.logo_light ?? assets?.logo_dark)) ?? null;
  // A tagline belongs to the name it was written for. A rebrand that changed
  // the name and not the tagline would otherwise keep printing the file's
  // tagline — the platform's own — at the foot of every export.
  const overridden = theme?.overridden ?? [];
  const taglineStands = !overridden.includes("name") || overridden.includes("tagline");
  const tagline = taglineStands ? resolve(theme?.brand.tagline) : "";
  const categorical = theme?.platform.dataViz?.categorical?.[scheme] ?? [];

  if (!keepTitle && productName && document.title !== productName) {
    document.title = productName;
  }

  return (
    <ThemeContext
      value={{
        theme,
        scheme,
        productName,
        tagline,
        footer: resolve(theme?.brand.emailFooter),
        assistantName: theme?.brand.assistantName || productName,
        welcomeTitle: resolve(theme?.brand.welcomeTitle),
        welcomeBody: resolve(theme?.brand.welcomeBody),
        logo,
        categorical,
      }}
    >
      {children}
    </ThemeContext>
  );
}
